import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SaveSchema = z.object({
  endpoint: z.string().url().max(1000),
  p256dh: z.string().min(1).max(500),
  auth: z.string().min(1).max(500),
  topics: z.array(z.enum(["orders", "offers", "new_products"])).min(1),
});

// Stores (or updates) a browser push subscription. Works for both logged-in
// users (associates the user id) and guests (user_id stays null).
export const saveSubscription = createServerFn({ method: "POST" })
  .inputValidator((d: z.input<typeof SaveSchema>) => SaveSchema.parse(d))
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const authHeader = getRequestHeader("authorization");

    let userId: string | null = null;
    if (authHeader?.startsWith("Bearer ")) {
      try {
        const { createClient } = await import("@supabase/supabase-js");
        const sb = createClient(
          process.env.SUPABASE_URL!,
          process.env.SUPABASE_PUBLISHABLE_KEY!,
          { auth: { persistSession: false, autoRefreshToken: false } },
        );
        const { data: u } = await sb.auth.getUser(authHeader.replace("Bearer ", ""));
        userId = u?.user?.id ?? null;
      } catch {
        userId = null;
      }
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Do a manual delete-then-insert instead of upsert/onConflict so the save
    // never depends on the API schema cache knowing about the unique
    // constraint on `endpoint`.
    await (supabaseAdmin as any)
      .from("push_subscriptions")
      .delete()
      .eq("endpoint", data.endpoint);
    const { error } = await (supabaseAdmin as any).from("push_subscriptions").insert({
      endpoint: data.endpoint,
      p256dh: data.p256dh,
      auth: data.auth,
      topics: data.topics,
      user_id: userId,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removeSubscription = createServerFn({ method: "POST" })
  .inputValidator((d: { endpoint: string }) =>
    z.object({ endpoint: z.string().url().max(1000) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await (supabaseAdmin as any).from("push_subscriptions").delete().eq("endpoint", data.endpoint);
    return { ok: true };
  });
